import { persistRecord, deleteRecord, surfaceWriteFailure } from '@/api/persist';

export const WRITE_DEBOUNCE_MS = 500;
export const WRITE_MAX_WAIT_MS = 2000;
export const RETRY_BASE_MS = 1000;
export const RETRY_CAP_MS = 15000;
export const MAX_ATTEMPTS = 8;

type Op = 'put' | 'delete';
type RejectFn = (status: number, body: { message?: string } | undefined) => void;
interface Pending { entity: string; id: string; op: Op; value: unknown; onReject?: RejectFn; onSuccess?: () => void; attempts: number }

const pending = new Map<string, Pending>();
/** `${entity}:${id}` → writes sent but not answered yet. */
const inFlight = new Map<string, number>();
/** Every write sent and not yet answered, so a caller can wait for them all (L5). */
const unanswered = new Set<Promise<unknown>>();

/** Whether this tab has a write to the record queued or still on its way (B2). */
export function isWritePending(entity: string, id: string): boolean {
  const key = `${entity}:${id}`;
  return pending.has(key) || (inFlight.get(key) ?? 0) > 0;
}
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let maxTimer: ReturnType<typeof setTimeout> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Coalesce a write by `${entity}:${id}`. Repeated calls overwrite op/value but
 * KEEP the first onReject (the true server baseline) and RESET attempts (a fresh
 * user edit is a new write, not a continuation of a stale retry). `onSuccess` runs
 * once the write has LANDED (2xx), after any retries; the latest one wins.
 */
export function scheduleWrite(
  entity: string,
  id: string,
  op: Op,
  value: unknown,
  onReject?: RejectFn,
  onSuccess?: () => void,
): void {
  const key = `${entity}:${id}`;
  const prev = pending.get(key);
  pending.set(key, { entity, id, op, value, onReject: prev ? prev.onReject : onReject, onSuccess, attempts: 0 });
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => flushWrites(), WRITE_DEBOUNCE_MS);
  if (!maxTimer) maxTimer = setTimeout(() => flushWrites(), WRITE_MAX_WAIT_MS);
}

/** Re-enqueue a write the server transiently refused, unless a newer write already superseded it. */
function reenqueueForRetry(w: Pending): void {
  const key = `${w.entity}:${w.id}`;
  if (pending.has(key)) return; // a newer user write wins
  const attempts = w.attempts + 1;
  if (attempts > MAX_ATTEMPTS) {
    // Retries exhausted — the write is genuinely lost. Surface it loudly NOW
    // (red chip + critical toast, see persist.surfaceWriteFailure), rather than
    // per-attempt, so a transient blip that the retries recovered stayed quiet.
    surfaceWriteFailure();
    return;
  }
  pending.set(key, { ...w, attempts });
  const delay = Math.min(RETRY_BASE_MS * 2 ** (attempts - 1), RETRY_CAP_MS);
  if (!retryTimer) {
    retryTimer = setTimeout(() => { retryTimer = null; flushWrites(); }, delay);
  }
}

/** Flush all pending writes now. Unload paths pass { keepalive:true } (no retry — the page is gone). */
export function flushWrites(opts?: { keepalive?: boolean }): void {
  void flushBatch(opts);
}

function flushBatch(opts?: { keepalive?: boolean }): Promise<unknown> {
  if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
  if (maxTimer) { clearTimeout(maxTimer); maxTimer = null; }
  if (pending.size === 0) return Promise.resolve();
  const batch = [...pending.values()];
  pending.clear();
  const settled: Promise<unknown>[] = [];
  for (const w of batch) {
    const outcome = w.op === 'delete'
      ? deleteRecord(w.entity, w.id, opts)
      : persistRecord(w.entity, w.id, w.value, w.onReject, opts);
    const done = Promise.resolve(outcome);
    const key = `${w.entity}:${w.id}`;
    inFlight.set(key, (inFlight.get(key) ?? 0) + 1);
    const landed = done.finally(() => {
      const n = (inFlight.get(key) ?? 1) - 1;
      if (n > 0) inFlight.set(key, n);
      else inFlight.delete(key);
    });
    void landed.catch(() => undefined);
    const tracked: Promise<unknown> = landed.catch(() => undefined).finally(() => unanswered.delete(tracked));
    unanswered.add(tracked);
    settled.push(done);
    if (!opts?.keepalive) {
      void done.then((o) => {
        if (o === 'transient') reenqueueForRetry(w);
        else if (o === 'ok') w.onSuccess?.();
      });
    }
  }
  return Promise.allSettled(settled);
}

/**
 * Flush the pending writes and resolve once the server has answered each of them
 * (never rejects). For an intent that READS a row the user just edited: send the
 * edit first so the intent's transaction sees it, instead of the edit's PUT
 * racing in after the intent and being refused as a stale revert (tv-2na.8.11).
 */
export function flushWritesAndWait(): Promise<void> {
  // L5 (tv-54xv): also the writes an EARLIER flush already sent and that are still
  // on their way (a late check-in / seat PUT on a slow line), not only the queued
  // ones — an intent must not race them.
  const already = [...unanswered];
  return Promise.all([flushBatch(), ...already]).then(() => undefined);
}

/** Flush when the browser regains connectivity. */
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => flushWrites());
}

/** Test hygiene: drop pending + timers without flushing. */
export function resetWriteQueue(): void {
  if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
  if (maxTimer) { clearTimeout(maxTimer); maxTimer = null; }
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  pending.clear();
  inFlight.clear();
  unanswered.clear();
}
